import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import TabNavigation from '../../components/ui/TabNavigation';
import BookingModal from '../../components/ui/BookingModal';
import MonthlyCalendarGrid from '../../components/ui/MonthlyCalendarGrid';
import WeekNavigator from './components/WeekNavigator';
import DailySummaryCard from './components/DailySummaryCard';
import StatsOverview from './components/StatsOverview';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import { formatDateForDB } from '../../utils/dateUtils';

const CalendarView = () => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date();
    date?.setDate(1);
    date?.setHours(0, 0, 0, 0);
    return date;
  });

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch customers from Supabase
  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase?.from('customers')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  // Fetch services from Supabase
  const fetchServices = async () => {
    try {
      const { data, error } = await supabase?.from('services')?.select('*')?.eq('is_available', true)?.order('name', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  // Fetch appointments from Supabase with retry logic for schema cache
  const fetchAppointments = async (retryCount = 0) => {
    try {
      const { data, error } = await supabase?.from('bookings')?.select(`
        *,
        services:service_id (
          name,
          description
        )
      `)?.order('date', { ascending: true });

      if (error) {
        // Handle schema cache error with retry
        if (error?.code === 'PGRST205' && retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          // console.log(`Schema cache not ready, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchAppointments(retryCount + 1);
        }
        throw error;
      }
      setAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      if (err?.code === 'PGRST205') {
        setError('Database schema is updating. Please refresh the page in a moment.');
      } else {
        setError('Failed to load appointments');
      }
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchServices(), fetchAppointments()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleNavigate = (direction) => {
    const newDate = new Date(currentMonth);
    newDate?.setMonth(newDate?.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const handleToday = () => {
    const date = new Date();
    date?.setDate(1);
    date?.setHours(0, 0, 0, 0);
    setCurrentMonth(date);
  };

  const handleTimeSlotClick = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setSelectedBooking(null);
    setIsBookingModalOpen(true);
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setSelectedDate(null);
    setSelectedTime(null);
    setIsBookingModalOpen(true);
  };

  const handleSaveBooking = async (bookingData) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        customer_id: bookingData?.customerId,
        service_id: bookingData?.serviceId || null,
        date: bookingData?.date,
        time: bookingData?.time,
        duration: parseInt(bookingData?.duration),
        service: bookingData?.service,
        notes: bookingData?.notes || '',
        status: bookingData?.status || 'scheduled',
        customer_name: bookingData?.customerName,
        customer_email: bookingData?.customerEmail,
        customer_phone: bookingData?.customerPhone
      };

      if (selectedBooking) {
        // Update existing booking
        const { data, error } = await supabase?.from('bookings')?.update(dbData)?.eq('id', selectedBooking?.id)?.select()?.single();

        if (error) throw error;

        setAppointments(prev =>
          prev?.map(apt => apt?.id === selectedBooking?.id ? data : apt)
        );
      } else {
        // Create new booking
        const { data, error } = await supabase?.from('bookings')?.insert([dbData])?.select()?.single();

        if (error) throw error;

        setAppointments(prev => [...prev, data]);
      }

      setIsBookingModalOpen(false);
      setSelectedBooking(null);
      setSelectedDate(null);
      setSelectedTime(null);
    } catch (err) {
      console.error('Error saving booking:', err);
      throw new Error('Failed to save booking. Please try again.');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      const { error } = await supabase?.from('bookings')?.delete()?.eq('id', bookingId);

      if (error) throw error;

      setAppointments(prev => prev?.filter(apt => apt?.id !== bookingId));
      setIsBookingModalOpen(false);
      setSelectedBooking(null);
    } catch (err) {
      console.error('Error deleting booking:', err);
      throw new Error('Failed to delete booking. Please try again.');
    }
  };

  const todayAppointments = useMemo(() => {
    const today = formatDateForDB(new Date());
    return appointments?.filter(apt => apt?.date === today);
  }, [appointments]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error text-lg mb-2">Error Loading Data</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => window.location?.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <div className="container mx-auto px-4 py-6 md:py-8 lg:py-10">
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Calendar View
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage your appointments and schedule
              </p>
            </div>
            <QuickActions onNewBooking={() => {
              setSelectedDate(null);
              setSelectedTime(null);
              setSelectedBooking(null);
              setIsBookingModalOpen(true);
            }} />
          </div>

          <StatsOverview appointments={appointments} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <WeekNavigator
                currentMonth={currentMonth}
                onNavigate={handleNavigate}
                onToday={handleToday}
              />

              <MonthlyCalendarGrid
                selectedDate={currentMonth}
                appointments={appointments}
                onDateClick={(date) => {
                  setSelectedDate(date);
                  setSelectedTime(null);
                  setSelectedBooking(null);
                  setIsBookingModalOpen(true);
                }}
                onBookingClick={handleBookingClick}
                onAppointmentClick={handleBookingClick}
              />
            </div>

            <div className="lg:col-span-1">
              <DailySummaryCard
                date={new Date()}
                appointments={todayAppointments}
                onBookingClick={handleBookingClick}
              />
            </div>
          </div>
        </div>
      </div>
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedBooking(null);
          setSelectedDate(null);
          setSelectedTime(null);
        }}
        booking={selectedBooking}
        onSave={handleSaveBooking}
        onDelete={handleDeleteBooking}
        customers={customers}
        services={services}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
      />
    </div>
  );
};

export default CalendarView;